import React, { useEffect, useState } from "react";
import {
  Anchor,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";

interface EmailReceiptModalProps {
  opened: boolean;
  onClose: () => void;
  /** The client's email on file, if any. */
  clientEmail?: string;
  /** Sends the receipt to the chosen address; resolves on success. */
  onSend: (email: string) => Promise<void>;
  loading?: boolean;
}

const isEmail = (v: string): boolean => /^\S+@\S+\.\S+$/.test(v.trim());

/**
 * Recipient picker for "Email Receipt": send to the client's on-file email, or
 * type a different one. When the client has no email on file it opens straight
 * into the custom-entry mode.
 */
export default function EmailReceiptModal({
  opened,
  onClose,
  clientEmail,
  onSend,
  loading = false,
}: EmailReceiptModalProps) {
  const [mode, setMode] = useState<"choose" | "custom">(
    clientEmail ? "choose" : "custom",
  );
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (opened) {
      setMode(clientEmail ? "choose" : "custom");
      setEmail("");
    }
  }, [opened, clientEmail]);

  return (
    <Modal opened={opened} onClose={onClose} title="Email receipt" centered>
      {mode === "choose" && clientEmail ? (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Send this receipt to the client&apos;s email on file:
          </Text>
          <Text fw={600}>{clientEmail}</Text>
          <Button
            color="var(--brand-teal)"
            loading={loading}
            onClick={() => onSend(clientEmail)}
          >
            Send to this email
          </Button>
          <Anchor
            component="button"
            type="button"
            size="sm"
            onClick={() => setMode("custom")}
          >
            Use a different email instead
          </Anchor>
        </Stack>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {clientEmail
              ? "Enter an email to send this receipt to."
              : "No email on file for this client — enter one to send the receipt."}
          </Text>
          <TextInput
            label="Recipient email"
            placeholder="client@example.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            type="email"
            data-autofocus
          />
          <Group justify="space-between">
            {clientEmail ? (
              <Anchor
                component="button"
                type="button"
                size="sm"
                onClick={() => setMode("choose")}
              >
                Use the email on file
              </Anchor>
            ) : (
              <span />
            )}
            <Group gap="sm">
              <Button variant="default" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                color="var(--brand-teal)"
                disabled={!isEmail(email)}
                loading={loading}
                onClick={() => onSend(email.trim())}
              >
                Send
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
